import React, {useState, useEffect, useRef} from 'react';
import {Tooltip} from "primereact/tooltip";
import {useNavigate, useParams} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {Panel} from "primereact/panel";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {classNames} from "primereact/utils";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import download from "js-file-download";

import HelpComponent from "../HelpComponent";
import {generateAccommodationCertificatePdf} from "../../util/signature";

const _FM_AccommodationShowComponent = (props) => {

  const {t} = props;
  const navigate = useNavigate();
  const {id, action, entity} = useParams();
  const [formData, setFormData] = useState({});
  const [countries, setCountries] = useState([]);
  const [paymentmethods, setPaymentmethods] = useState([]);
  const [CFormData, setCFormData] = useState({});
  const [CFields, setCFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [hotelConnectSyncLoading, setHotelConnectSyncLoading] = useState(false);
  const [hotelConnectTestCertificateLoading, setHotelConnectTestCertificateLoading] = useState(false);
  const [dailyClosureStatus, setDailyClosureStatus] = useState(null);
  const pollTimerRef = useRef(null);
  const selectedCountryLabel = countries.find((country) => country.code === formData.country)?.name
    || (formData.country ? formData.country.toUpperCase() : 'HU');
  const countrySpecificItems = formData.country ? formData["accommodation_" + formData.country.toLowerCase() + "s"] : null;
  const hasCountrySpecificRecord = Array.isArray(countrySpecificItems) && countrySpecificItems[0];
  const countryCode = (formData?.country || '').toUpperCase();
  const isHuCountrySpecific = countryCode === 'HU';
  const ifaType = CFormData?.ifa_type;
  const normalizedIfaType = typeof ifaType === 'string' ? ifaType.toLowerCase().trim() : '';
  const isNoneLikeIfaType = !normalizedIfaType
    || normalizedIfaType === 'none'
    || /^im([1-9]|10|11)$/.test(normalizedIfaType);
  const visibleCountrySpecificFields = (CFields || []).filter((field) => {
    if (!field?.name) return false;

    const isIfaNightField = field.name === 'ifa_night_fee' || field.name === 'ifa_night';
    const isIfaPercentField = field.name === 'ifa_percent';
    if (isHuCountrySpecific && isNoneLikeIfaType && (isIfaNightField || isIfaPercentField)) return false;
    if (isHuCountrySpecific && normalizedIfaType === 'percent' && isIfaNightField) return false;
    if (isHuCountrySpecific && normalizedIfaType === 'fix_amount' && isIfaPercentField) return false;

    const value = CFormData[field.name];
    if (typeof value === 'number') return true;
    if (typeof value === 'string') return value.trim() !== '';
    return false;
  });
  const hasCountrySpecificSettings = Boolean(CFields?.[0]) && Boolean(hasCountrySpecificRecord) && visibleCountrySpecificFields.length > 0;
  const hasAccommodationHu = Boolean(
    CFormData?.id
    || formData?.accommodation_hus?.[0]?.id
    || formData?.accommodation_hu?.id
    || formData?.accommodation_hu_id
  );
  const displayValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return t(value.toString());
    if (typeof value === 'string') return t(value);
    if (Array.isArray(value)) return value.map((item) => displayValue(item)).filter(Boolean).join(', ');
    if (typeof value === 'object') {
      if (typeof value.label === 'string') return t(value.label);
      if (typeof value.name === 'string') return t(value.name);
      if (typeof value.code === 'string') return value.code;
      return '';
    }
    return String(value);
  };

  const headers = {
    'Authorization': 'Bearer ' + Cookies.get("userToken"),
    "Sys-name": Cookies.get("instanceId"),
    "Preferred-locale": Cookies.get("language")
  };

  const getFirstFilledValue = (sources, candidates) => {
    for (const source of sources) {
      if (!source) continue;
      for (const key of candidates) {
        const value = source[key];
        if (value === null || value === undefined) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
      }
    }
    return '';
  };

  const getHotelConnectStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'success') {
      return {badgeClass: 'p-badge-success', label: t('hotelconnect_status_success'), style: {}};
    }
    if (normalizedStatus === 'failed') {
      return {badgeClass: '', label: t('hotelconnect_status_failed'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'skipped') {
      return {badgeClass: 'p-badge-secondary', label: t('hotelconnect_status_skipped'), style: {}};
    }
    return {badgeClass: '', label: t('hotelconnect_status_not_started'), style: neutralStyle};
  };

  const getDailyClosureStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'up_to_date') {
      return {label: t('daily_closure_status_up_to_date'), style: {backgroundColor: '#0abb87', color: '#ffffff'}};
    }
    if (normalizedStatus === 'attention') {
      return {label: t('daily_closure_status_attention'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'pending') {
      return {label: t('daily_closure_status_pending'), style: neutralStyle};
    }
    if (normalizedStatus === 'not_ready') {
      return {label: t('daily_closure_status_not_ready'), style: neutralStyle};
    }
    if (normalizedStatus === 'not_applicable') {
      return {label: t('daily_closure_status_not_applicable'), style: neutralStyle};
    }
    return {label: t('daily_closure_status_not_started'), style: neutralStyle};
  };

  const getDailyClosureAttemptStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'success') {
      return {label: t('hotelconnect_status_success'), style: {backgroundColor: '#0abb87', color: '#ffffff'}};
    }
    if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
      return {label: t('hotelconnect_status_failed'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'skipped') {
      return {label: t('hotelconnect_status_skipped'), style: neutralStyle};
    }
    return {label: t('daily_closure_status_not_started'), style: neutralStyle};
  };

  const formatDateTimeValue = (value) => {
    if (!value) {
      return t('hotelconnect_certificate_not_uploaded_yet');
    }
    const parsedDate = moment(value);
    if (!parsedDate.isValid()) {
      return String(value);
    }
    return parsedDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const formatDailyClosureDateTimeValue = (value) => {
    if (!value) {
      return '-';
    }
    const parsedDate = moment(value);
    if (!parsedDate.isValid()) {
      return String(value);
    }
    return parsedDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const getHuDataValue = (keys = []) => getFirstFilledValue([CFormData, formData], keys);
  const hotelConnectAccommodationId = getHuDataValue(['hotel_connect_accommodation_id', 'hotelConnectAccommodationId']);
  const hotelConnectRegistrationStatus = getHuDataValue(['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']);
  const hotelConnectCsrStatus = getHuDataValue(['hotel_connect_csr_status', 'hotelConnectCsrStatus']);
  const hotelConnectCertificateStatus = getHuDataValue(['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']);
  const hotelConnectCertificateUploadedAt = getHuDataValue(['hotel_connect_certificate_uploaded_at', 'hotelConnectCertificateUploadedAt']);
  const hasHotelConnectCertificateUploadedAt = hotelConnectCertificateUploadedAt !== null
    && hotelConnectCertificateUploadedAt !== undefined
    && String(hotelConnectCertificateUploadedAt).trim() !== '';
  const hasHotelConnectAccommodationId = hotelConnectAccommodationId !== null
    && hotelConnectAccommodationId !== undefined
    && String(hotelConnectAccommodationId).trim() !== '';
  const normalizedRegistrationStatus = String(hotelConnectRegistrationStatus || '').toLowerCase();
  const normalizedCsrStatus = String(hotelConnectCsrStatus || '').toLowerCase();
  const normalizedCertificateStatus = String(hotelConnectCertificateStatus || '').toLowerCase();
  const shouldShowHotelConnectSyncRetry = normalizedRegistrationStatus === 'failed';
  const hotelConnectRegistrationError = getHuDataValue(['hotel_connect_registration_error', 'hotelConnectRegistrationError']);
  const hotelConnectCsrError = getHuDataValue(['hotel_connect_csr_error', 'hotelConnectCsrError']);
  const hotelConnectCertificateError = getHuDataValue(['hotel_connect_certificate_error', 'hotelConnectCertificateError']);
  const accommodationShowSyncIdentifier = formData?.id || id || 'new';
  const currentUrl = (typeof window !== 'undefined' ? window.location.href : '').toLowerCase();
  const isTestCertificateHost = currentUrl.includes('test.felhomatrac') || currentUrl.includes('felhomatrac.local');
  const isCsrReady = normalizedRegistrationStatus === 'success' && normalizedCsrStatus === 'success';
  const canGenerateHotelConnectTestCertificate = isTestCertificateHost
    && hasAccommodationHu
    && Boolean(id)
    && normalizedRegistrationStatus === 'success'
    && normalizedCsrStatus === 'success';
  const hasHotelConnectFailure = [normalizedRegistrationStatus, normalizedCsrStatus, normalizedCertificateStatus].includes('failed');
  const renderLabelWithErrorIcon = (rowKey, label, errorText) => (
    <div className="p-d-flex p-ai-center" style={{gap: '0.4rem'}}>
      {errorText &&
        <>
          <span
            className={`tt-hotelconnect-error-${rowKey}-show-${accommodationShowSyncIdentifier}`}
            data-pr-tooltip={errorText}
            style={{display: 'inline-flex', color: '#f44336'}}
          >
            <i className="pi pi-info-circle" style={{fontSize: '0.8rem'}}></i>
          </span>
          <Tooltip target={`.tt-hotelconnect-error-${rowKey}-show-${accommodationShowSyncIdentifier}`}/>
        </>
      }
      <span>{label}</span>
    </div>
  );

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const downloadCertificatePdf = async () => {
    try {
      setCertificateLoading(true);
      let operatorData = formData?.operator_data || {};
      if (operatorData?.id) {
        const operatorResponse = await axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "operator-data/" + operatorData.id,
          params: {},
          headers
        });
        operatorData = operatorResponse?.data?.data || operatorData;
      }

      let ntakServiceId = getFirstFilledValue(
        [formData, CFormData],
        ['ntak_adatszolgaltatasi_azon', 'ntak_service_id', 'ntak_id']
      );
      let ntakRegistrationNumber = getFirstFilledValue(
        [formData, CFormData],
        ['ntak_regisztracio_szam', 'ntak_registration_number', 'ntak_registration_id']
      );

      if ((!ntakServiceId || !ntakRegistrationNumber) && id) {
        try {
          const huResponse = await axios({
            method: 'get',
            url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-hu/" + id,
            params: {},
            headers
          });
          const huData = huResponse?.data?.data || huResponse?.data || {};
          if (!ntakServiceId) {
            ntakServiceId = getFirstFilledValue([huData], ['ntak_adatszolgaltatasi_azon', 'ntak_service_id', 'ntak_id']);
          }
          if (!ntakRegistrationNumber) {
            ntakRegistrationNumber = getFirstFilledValue([huData], ['ntak_regisztracio_szam', 'ntak_registration_number', 'ntak_registration_id']);
          }
        } catch (error) {
          // A PDF ettől még elkészülhet, csak hiányzó mezőkkel.
        }
      }

      let signImageDataUrl = '';
      try {
        const signResponse = await axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "sign-image",
          responseType: 'blob',
          params: {},
          headers
        });
        signImageDataUrl = await blobToDataUrl(signResponse.data);
      } catch (error) {
        window.App.toastShow('warn', t('warning'), t('download_sign_image_failed'));
      }

      const accommodationAddress = [formData?.zip_code, formData?.city, formData?.street_and_number]
        .filter((item) => String(item || '').trim() !== '')
        .join(' ');
      const operatorSeat = [operatorData?.zip_code, operatorData?.registered_office_city, operatorData?.street_and_house_number]
        .filter((item) => String(item || '').trim() !== '')
        .join(', ');

      await generateAccommodationCertificatePdf({
        accommodationName: formData?.name,
        accommodationAddress,
        ntakServiceId,
        ntakRegistrationNumber,
        operatorCompanyName: operatorData?.company_name,
        operatorSeat,
        operatorTaxNumber: operatorData?.tax_number,
        signImageDataUrl
      });
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setCertificateLoading(false);
    }
  };

  const fetchAccommodationAndCountrySpecificData = async () => {
    if (!id) {
      return null;
    }
    const accommodationResponse = await axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/" + id,
      params: {},
      headers
    });
    const accommodationData = accommodationResponse.data.data;
    setFormData(accommodationData);

    let huData = null;
    try {
      const huResponse = await axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-hu/" + id,
        params: {},
        headers
      });
      huData = huResponse?.data?.data || huResponse?.data || null;
      if (huData) {
        setCFormData((prev) => ({...prev, ...huData}));
      }
    } catch (error) {
      // Lehet, hogy még nincs HU rekord.
    }

    return {
      accommodationData,
      huData
    };
  };

  const startHotelConnectSyncPolling = async (attempt = 0) => {
    const maxAttempts = 12;
    const pollIntervalMs = 5000;
    try {
      const loadedData = await fetchAccommodationAndCountrySpecificData();
      const source = loadedData?.huData || loadedData?.accommodationData || {};
      const registrationStatus = String(getFirstFilledValue([source], ['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']) || '').toLowerCase();
      const csrStatus = String(getFirstFilledValue([source], ['hotel_connect_csr_status', 'hotelConnectCsrStatus']) || '').toLowerCase();
      const certificateStatus = String(getFirstFilledValue([source], ['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']) || '').toLowerCase();
      const finalStates = ['success', 'failed', 'skipped'];
      const isFinished = finalStates.includes(registrationStatus)
        && finalStates.includes(csrStatus)
        && (certificateStatus === '' || finalStates.includes(certificateStatus));

      if (isFinished || attempt >= maxAttempts) {
        pollTimerRef.current = null;
        return;
      }
    } catch (error) {
      if (attempt >= maxAttempts) {
        pollTimerRef.current = null;
        return;
      }
    }

    pollTimerRef.current = setTimeout(() => {
      startHotelConnectSyncPolling(attempt + 1);
    }, pollIntervalMs);
  };

  const handleHotelConnectSyncRetry = async () => {
    if (!id || !hasAccommodationHu) {
      return;
    }
    try {
      setHotelConnectSyncLoading(true);
      await axios({
        method: 'post',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + id + "/hotelconnect-sync",
        params: {},
        headers
      });
      window.App.toastShow('info', t('info'), t('hotelconnect_sync_started_message'));
      startHotelConnectSyncPolling(0);
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setHotelConnectSyncLoading(false);
    }
  };

  const handleHotelConnectTestCertificateDownload = async () => {
    if (!canGenerateHotelConnectTestCertificate) {
      return;
    }
    try {
      setHotelConnectTestCertificateLoading(true);
      const response = await axios({
        method: 'post',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + id + "/hotelconnect-test-certificate",
        params: {},
        responseType: 'blob',
        headers
      });
      download(response.data, 'hotelconnect_test_certificate.pem');
      window.App.toastShow('success', t('success'), t('download'));
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setHotelConnectTestCertificateLoading(false);
    }
  };

  const loadDailyClosureStatus = async (accommodationId) => {
    if (!accommodationId) {
      setDailyClosureStatus(null);
      return;
    }
    try {
      const response = await axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/hotelconnect-ntak-napizaras-status",
        params: {},
        headers
      });
      const accommodations = response?.data?.data?.accommodations || [];
      const selectedStatus = accommodations.find((item) => String(item?.accommodationId) === String(accommodationId)) || null;
      setDailyClosureStatus(selectedStatus);
    } catch (error) {
      setDailyClosureStatus(null);
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    }
  };

  useEffect(() => {
    if (formData.country) {
      let countryObjectName = "accommodation_" + formData.country.toLowerCase() + "s";
      if (formData[countryObjectName] && formData[countryObjectName][0] && formData[countryObjectName][0].id) {
        axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-" + formData.country.toLowerCase() + "/" + formData[countryObjectName][0].id,
          params: {},
          headers: {
            'Authorization': 'Bearer ' + Cookies.get("userToken"),
            "Sys-name": Cookies.get("instanceId"),
            "Preferred-locale": Cookies.get("language")
          }
        })
          .then(response => {
            setCFormData(response.data.data);
          })
          .catch(error => {
            window.App.toastShow('error', t('error'), error.toString(), '', error.response);
          })
      }
    }
  }, [formData._country_specifics])

  useEffect(() => {
    if (formData.country && (formData.country.toLowerCase() == Object.keys(formData._country_specifics)[0].toLowerCase())) {
      const transformedCountry = formData.country ? formData.country.charAt(0).toUpperCase()
        + formData.country.slice(1).toLowerCase() : "";
      axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "info/Accommodation" + transformedCountry,
        params: {},
        headers: {
          'Authorization': 'Bearer ' + Cookies.get("userToken"),
          "Sys-name": Cookies.get("instanceId"),
          "Preferred-locale": Cookies.get("language")
        }
      })
        .then(response => {
          setCFields(response.data.editable_fields);
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
    }
  }, [formData.country])

  useEffect(() => {
    axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "dictionary/country",
      params: {},
      headers: {
        'Authorization': 'Bearer ' + Cookies.get("userToken"),
        "Sys-name": Cookies.get("instanceId"),
        "Preferred-locale": Cookies.get("language")
      }
    })
      .then(response => {
        setCountries(response.data.data || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "payment-method",
      params: {},
      headers: {
        'Authorization': 'Bearer ' + Cookies.get("userToken"),
        "Sys-name": Cookies.get("instanceId"),
        "Preferred-locale": Cookies.get("language")
      }
    })
      .then(response => {
        setPaymentmethods(response.data?.data?.items || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    if (id) {
      setLoading(true)
      axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/" + id,
        params: {},
        headers: {
          'Authorization': 'Bearer ' + Cookies.get("userToken"),
          "Sys-name": Cookies.get("instanceId"),
          "Preferred-locale": Cookies.get("language")
        }
      })
        .then(response => {
          setLoading(false)
          setFormData(response.data.data);
          loadDailyClosureStatus(response?.data?.data?.id || id);
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
          setLoading(false)
        })
    }
  }, [])

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className={"p-grid p-mt-3"}>
        <div className={"p-col p-col-align-center"}>
          <h2 className={"p-m-0"}>
        {formData.name ? <span className={"p-text-nowrap"}>{formData?.name}<span className={"tt-auto-invoice p-ml-2"}
                                                                                 data-pr-tooltip={(formData?.auto_invoice ?? formData?.auto_invoice_on_check_out) === 'CREATE_AND_SYNC' ? t('auto_invoice_tooltip_active') : t('auto_invoice_tooltip_inactive')}><svg
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={"auto-invoice-icon"} style={{
          color: (formData?.auto_invoice ?? formData?.auto_invoice_on_check_out) === 'CREATE_AND_SYNC' ? '#0abb87' : '#dddddd',
          position: 'relative',
          bottom: '-3px'
        }}><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8"/><path d="M5 21l2.5-1.5L10 21l2.5-1.5L14 21l1.5-1"/><path
          d="M9 7h6"/><path d="M9 11h4"/><path d="M18 14a3 3 0 1 0 3 3"/><path d="M21 20v-3h-3"/></svg></span><Tooltip
          target=".tt-auto-invoice"/></span> : t('accommodation_show')}
          </h2>
        </div>
        <div className={"p-col p-text-right p-col-align-center"}>
          <Button type="button"
                  aria-label={t('certificate_download')}
                  label={t('certificate_download')}
                  icon={"pi pi-download"}
                  loading={certificateLoading}
                  onClick={downloadCertificatePdf}
                  className="p-button-secondary p-ml-2">
          </Button>
          <Button type="button" aria-label={t('edit')}
                  icon={"pi pi-pencil"}
                  onClick={() => {
                    navigate('/accommodation/')
                    setTimeout(() => {
                      navigate('/accommodation/update/' + id)
                    }, 500)
                  }}
                  className="p-button-warning p-ml-2">
          </Button>
          <Button type="button"
                  icon={"pi pi-times"}
                  onClick={() => {
                    navigate(-1)
                  }}
                  className="p-button-secondary close p-ml-2">
          </Button>
        </div>
      </div>
      <div className={"p-grid"}>
        <div className={"p-col"}>
          <small className={"p-text-uppercase"}>
            {t('short_name')}
          </small>
          <p className={"p-mt-0 p-mb-0 p-text-bold"}>
            {formData.short_name}
          </p>
        </div>
        <div className={"p-col"}>
          <small className={"p-text-uppercase"}>
            {t('created_at')}
          </small>
          <p className={"p-mt-0 p-mb-0 p-text-bold"}>
            {moment.utc(formData.created_at_snapshot).local().format(t('YYYY-MM-DD HH:mm:ss'))}
          </p>
        </div>
        <div className={"p-col"}>
          <small className={"p-text-uppercase"}>
            {t('updated_at')}
          </small>
          <p className={"p-mt-0 p-mb-0 p-text-bold"}>
            {moment.utc(formData.updated_at_snapshot).local().format(t('YYYY-MM-DD HH:mm:ss'))}
          </p>
        </div>
      </div>
      {/*<HelpComponent type={"site"} position={"IN_ACTIVE_BOX"}/>*/}
      <HelpComponent type={"show_box"}/>
      <TabView className={"p-mt-3"} renderActiveOnly={false}>
        <TabPanel header={t('accommodation_form')}>
          <HelpComponent type={"tab"} tabNumber={1}/>
          <Panel className={"p-mt-0"}>
            <h3 className={"p-mt-0"}>{t('accommodation_identification')}</h3>
            <HelpComponent type={"section"} tabNumber={1} sectionNumber={1}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="name">{t('name')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.name}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-3"}>
                <div className="p-field p-fluid">
                  <label htmlFor="short_name">{t('short_name')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.short_name}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-3"}>
                <div className="p-field p-fluid">
                  <label htmlFor="country">{t('country')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.country}
                    </span>
                </div>
              </div>
            </div>
          </Panel>
          <Panel className={"p-mt-3"}>
            <h3 className={"p-mt-0"}>{t('accommodation_location')}</h3>
            <HelpComponent type={"section"} tabNumber={1} sectionNumber={2}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-3"}>
                <div className="p-field p-fluid">
                  <label htmlFor="zip_code">{t('zip_code')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.zip_code}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-3"}>
                <div className="p-field p-fluid">
                  <label htmlFor="city">{t('city')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.city}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="street_and_number">{t('street_and_number')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.street_and_number}
                    </span>
                </div>
              </div>
            </div>
          </Panel>
          <Panel className={"p-mt-3"}>
            <h3 className={"p-mt-0"}>{t('accommodation_contact')}</h3>
            <HelpComponent type={"section"} tabNumber={1} sectionNumber={3}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="contact_name">{t('contact_name')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.contact_name}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="contact_email">{t('contact_email')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.contact_email}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="contact_phone">{t('contact_phone')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.contact_phone}
                    </span>
                </div>
              </div>
            </div>
          </Panel>
          <Panel className={"p-mt-3"}>
            <h3 className={"p-mt-0"}>{t('accommodation_operation')}</h3>
            <HelpComponent type={"section"} tabNumber={1} sectionNumber={4}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="operator_data">{t('operator_data')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.operator_data?.company_name}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-3"}>
                <div className="p-field p-fluid">
                  <label htmlFor="currency">{t('currency')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.currency_code}
                    </span>
                </div>
              </div>
            </div>
          </Panel>
        </TabPanel>
        <TabPanel header={t('settings')}>
          <HelpComponent type={"tab"} tabNumber={2}/>
          <Panel>
            <h3 className={"p-mt-0"}>{t('settings')}</h3>
            <HelpComponent type={"section"} tabNumber={2} sectionNumber={1}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="child_age_limit">{t('child_age_limit')}</label>
                  <span className="p-d-block p-text-bold">
                {formData.child_age_limit}
              </span>
                </div>
              </div>
              {/*<div className={"p-col-12 p-lg-4"}>*/}
              {/*  <div className="p-field p-fluid">*/}
              {/*    <label htmlFor="readyness_checklist">{t('accommodationchecklist')}</label>*/}
              {/*    <span className="p-d-block p-text-bold">*/}
              {/*  {formData.readiness_checklist?.name}*/}
              {/*</span>*/}
              {/*  </div>*/}
              {/*</div>*/}
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="daily_close_behavior">{t('daily_close_behavior')}</label>
                  <span className="p-d-block p-text-bold">
                {t(formData?.daily_close_behavior?.toLowerCase())}
              </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="auto_check_in_days_after_confirmed">{t('auto_check_in_time')}</label>
                  <span className="p-d-block p-text-bold">
                {formData.auto_check_in_time &&
                  <>
                    {moment.utc(formData.auto_check_in_time).local().subtract(1, 'hours').format('HH:mm')}
                  </>
                }
              </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="auto_check_out_time">{t('auto_check_out_time')}</label>
                  <span className="p-d-block p-text-bold">
                {formData.auto_check_in_time &&
                  <>
                    {moment.utc(formData.auto_check_out_time).local().subtract(1, 'hours').format('HH:mm')}
                  </>
                }
              </span>
                </div>
              </div>
              {/*<div className={"p-col-12 p-lg-4"}>*/}
              {/*  <div className="p-field p-fluid">*/}
              {/*    <label htmlFor="guest_data_from_guest_request">{t('guest_data_from_guest_request')}</label>*/}
              {/*    <span className="p-d-block p-text-bold">*/}
              {/*      {t(formData?.guest_data_from_guest_request?.toString().toLowerCase())}*/}
              {/*    </span>*/}
              {/*  </div>*/}
              {/*</div>*/}
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="api">{t('api')}</label>
                  <span className="p-d-block p-text-bold">
                {t(formData?.api?.toString().toLowerCase())}
              </span>
                </div>
              </div>
            </div>
          </Panel>
          <Panel className={"p-mt-3"}>
            <h3 className={"p-mt-0"}>{t('booking_data_request_tab')}</h3>
            <HelpComponent type={"section"} tabNumber={2} sectionNumber={2}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="customer_data_email_send_mode">{t('customer_data_email_send_mode')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.customer_data_email_send_mode && t(formData.customer_data_email_send_mode.toLowerCase())}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label
                    htmlFor="customer_data_email_auto_repeat_send">{t('customer_data_email_auto_repeat_send')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.customer_data_email_auto_repeat_send && t(formData.customer_data_email_auto_repeat_send.toLowerCase())}
                    </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="customer_data_email_reply_to">{t('customer_data_email_reply_to')}</label>
                  <span className="p-d-block p-text-bold">
                      {formData.customer_data_email_reply_to}
                    </span>
                </div>
              </div>
            </div>
          </Panel>
        </TabPanel>
        <TabPanel header={t('billing_tab')}>
          <HelpComponent type={"tab"} tabNumber={3}/>
          <Panel>
            <h3 className={"p-mt-0"}>{t('billing_tab')}</h3>
            <HelpComponent type={"section"} tabNumber={3} sectionNumber={1}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="default_payment_method">{t('default_payment_method')}</label>
                  <span className="p-d-block p-text-bold">
                {
                  formData?.default_payment_method?.name
                    ? window.App.getTranslatedLabel(formData.default_payment_method.name)?.replaceAll('_', ' ')
                    : (
                      paymentmethods.find((method) => String(method.id) === String(formData?.default_payment_method?.id || formData?.default_payment_method))?.name
                        ? window.App.getTranslatedLabel(paymentmethods.find((method) => String(method.id) === String(formData?.default_payment_method?.id || formData?.default_payment_method))?.name)?.replaceAll('_', ' ')
                        : (formData?.default_payment_method?.label || formData?.default_payment_method?.id || displayValue(formData?.default_payment_method) || t('not_set'))
                    )
                }
              </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="auto_invoice">{t('auto_invoice_on_check_out')}</label>
                  <span className="p-d-block p-text-bold">
                {(formData?.auto_invoice ?? formData?.auto_invoice_on_check_out)
                  && t((formData?.auto_invoice ?? formData?.auto_invoice_on_check_out).toLowerCase())}
              </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="advance_invoice_trigger">{t('advance_invoice_trigger')}</label>
                  <span className="p-d-block p-text-bold">
                {formData?.advance_invoice_trigger && t(formData.advance_invoice_trigger.toLowerCase())}
              </span>
                </div>
              </div>
              <div className={"p-col-12 p-lg-4"}>
                <div className="p-field p-fluid">
                  <label htmlFor="advance_invoice_strategy">{t('advance_invoice_strategy')}</label>
                  <span className="p-d-block p-text-bold">
                {formData?.advance_invoice_trigger === 'NONE'
                  ? t('advance_invoice_disabled_helper')
                  : (formData?.advance_invoice_strategy && t(formData.advance_invoice_strategy.toLowerCase()))}
              </span>
                </div>
              </div>
              {formData?.advance_invoice_trigger && formData.advance_invoice_trigger !== 'NONE'
                && ['FIXED_AMOUNT', 'PERCENTAGE'].includes(formData?.advance_invoice_strategy) && (
                <div className={"p-col-12 p-lg-4"}>
                  <div className="p-field p-fluid">
                    <label htmlFor="advance_invoice_value">{t('advance_invoice_value')}</label>
                    <span className="p-d-block p-text-bold">
                {formData.advance_invoice_value === null || formData.advance_invoice_value === undefined || formData.advance_invoice_value === ''
                  ? ''
                  : (Number.isFinite(Number(formData.advance_invoice_value))
                    ? (Number.isInteger(Number(formData.advance_invoice_value))
                      ? String(Number(formData.advance_invoice_value))
                      : Number(formData.advance_invoice_value).toFixed(2))
                    : formData.advance_invoice_value)}
                      %
              </span>
                  </div>
                </div>
              )}
            </div>
          </Panel>
        </TabPanel>
        <TabPanel header={t('channel_manager')}>
          <HelpComponent type={"tab"} tabNumber={4}/>
          <Panel>
            <h3 className={"p-mt-0"}>{t('channel_manager')}</h3>
            <HelpComponent type={"section"} tabNumber={4} sectionNumber={1}/>
            <div className={"p-grid"}>
              <div className={"p-col-12 p-lg-12"}>
                <div className="p-field p-fluid">
                  <label htmlFor="channel_manager">{t('channel_manager')}</label>
                  <span className="p-d-block p-text-bold">
                {formData.channel_manager}
              </span>
                </div>
              </div>

              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="unique_id">{t('unique_id')}</label>
                  <span className="p-d-block p-text-bold">
                {formData.unique_id}
              </span>
                </div>
              </div>
              {formData.channel_manager === 'EZEE' &&
                <>
                  <div className={"p-col-12 p-lg-6"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="e_zee_centrix_email">{t('ezee_centrix_email')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.e_zee_centrix_email}
                </span>
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-6"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="e_zee_centrix_hotel_code">{t('ezee_centrix_hotel_code')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.e_zee_centrix_hotel_code}
                </span>
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-6"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="e_zee_centrix_auth_key">{t('ezee_centrix_auth_key')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.e_zee_centrix_auth_key}
                </span>
                    </div>
                  </div>
                </>
              }
              {formData.channel_manager === 'FELHOMATRAC_ICAL_FEED' &&
                <>
                  <div className={"p-col-12 p-lg-12"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="airbnb_calendar_url">{t('airbnb_calendar_url')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.airbnb_calendar_url}
                </span>
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-12"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="channel_manager_export_url">{t('channel_manager_export_url')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.channel_manager_export_url}
                </span>
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-12"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="szallas_calendar_url">{t('szallas_calendar_url')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.szallas_calendar_url}
                </span>
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-12"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="booking_calendar_url">{t('booking_calendar_url')}</label>
                      <span className="p-d-block p-text-bold">
                  {formData.booking_calendar_url}
                </span>
                    </div>
                  </div>
                </>
              }
            </div>
          </Panel>
        </TabPanel>
        <TabPanel header={t('country_specific_settings')}>
          <HelpComponent type={"tab"} tabNumber={5}/>
          <Panel>
            <h3 className={"p-mt-0"}>
              {selectedCountryLabel}
            </h3>
            <HelpComponent type={"section"} tabNumber={5} sectionNumber={1}/>
            {!hasCountrySpecificSettings &&
              <p className={"p-my-2"}>{t('no_country_specific_settings')}</p>
            }
            {hasCountrySpecificSettings &&
              <div className={"p-grid"}>
                {visibleCountrySpecificFields.map((field, index) => (
                  <div className={"p-col-12 p-lg-6"} key={index}>
                    <div className="p-field p-fluid">
                      <label htmlFor={field.name}>{t(field.name)}</label>
                      <span className="p-d-block p-text-bold">
                        {displayValue(CFormData[field.name])}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            }
            {isHuCountrySpecific && hasAccommodationHu &&
              <>
                <hr className={"p-my-3"}/>
                <h3 className={"p-mt-5"}>{t('ntak_validation')}</h3>
                <div className={"p-grid"}>
                  <div className={"p-col-12"}>
                    {isCsrReady &&
                      <HelpComponent
                        type={"custom_message"}
                        level={"LEVEL_SUCCESS"}
                        message={t('hotelconnect_csr_ready_hint')}
                      />
                    }
                    {hasHotelConnectFailure &&
                      <HelpComponent
                        type={"custom_message"}
                        level={"LEVEL_DANGER"}
                        message={t('hotelconnect_sync_failed_warning')}
                      />
                    }
                    <ul className="p-pl-0 p-mt-3 p-mb-0" style={{listStyle: 'none'}}>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          {renderLabelWithErrorIcon(
                            'registration',
                            t('hotelconnect_registration_status_label'),
                            hotelConnectRegistrationError
                          )}
                          <span
                            className={classNames(
                              'p-badge',
                              getHotelConnectStatusMeta(hotelConnectRegistrationStatus).badgeClass,
                              `tt-hotelconnect-retry-accommodation-show-${accommodationShowSyncIdentifier}`
                            )}
                            data-pr-tooltip={shouldShowHotelConnectSyncRetry ? t('hotelconnect_sync_retry_label') : undefined}
                            role={shouldShowHotelConnectSyncRetry ? 'button' : undefined}
                            tabIndex={shouldShowHotelConnectSyncRetry ? (hotelConnectSyncLoading ? -1 : 0) : undefined}
                            style={{
                              ...getHotelConnectStatusMeta(hotelConnectRegistrationStatus).style,
                              width: '110px',
                              minWidth: '110px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              gap: '0.35rem',
                              cursor: shouldShowHotelConnectSyncRetry && !hotelConnectSyncLoading ? 'pointer' : 'default'
                            }}
                            onClick={() => {
                              if (!shouldShowHotelConnectSyncRetry || hotelConnectSyncLoading) {
                                return;
                              }
                              handleHotelConnectSyncRetry();
                            }}
                            onKeyDown={(event) => {
                              if (!shouldShowHotelConnectSyncRetry || hotelConnectSyncLoading) {
                                return;
                              }
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleHotelConnectSyncRetry();
                              }
                            }}
                          >
                            <span>{getHotelConnectStatusMeta(hotelConnectRegistrationStatus).label}</span>
                            {shouldShowHotelConnectSyncRetry &&
                              <>
                                <i
                                  className={`pi ${hotelConnectSyncLoading ? 'pi-spin pi-spinner' : 'pi-refresh'}`}
                                  style={{color: 'inherit', fontSize: '0.8rem'}}
                                />
                                <Tooltip
                                  target={`.tt-hotelconnect-retry-accommodation-show-${accommodationShowSyncIdentifier}`}
                                  position="left"
                                />
                              </>
                            }
                          </span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          {renderLabelWithErrorIcon(
                            'csr',
                            t('hotelconnect_csr_status_label'),
                            hotelConnectCsrError
                          )}
                          <span
                            className={classNames('p-badge', getHotelConnectStatusMeta(hotelConnectCsrStatus).badgeClass)}
                            style={{
                              ...getHotelConnectStatusMeta(hotelConnectCsrStatus).style,
                              width: '110px',
                              minWidth: '110px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700
                            }}
                          >
                            {getHotelConnectStatusMeta(hotelConnectCsrStatus).label}
                          </span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          {renderLabelWithErrorIcon(
                            'certificate',
                            t('hotelconnect_certificate_status_label'),
                            hotelConnectCertificateError
                          )}
                          <span
                            className={classNames('p-badge', getHotelConnectStatusMeta(hotelConnectCertificateStatus).badgeClass)}
                            style={{
                              ...getHotelConnectStatusMeta(hotelConnectCertificateStatus).style,
                              width: '110px',
                              minWidth: '110px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700
                            }}
                          >
                            {getHotelConnectStatusMeta(hotelConnectCertificateStatus).label}
                          </span>
                        </div>
                      </li>
                      {hasHotelConnectCertificateUploadedAt &&
                        <li className="p-mb-3">
                          <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                            <span>{t('hotelconnect_certificate_uploaded_at_label')}</span>
                            <span className="p-text-bold">{formatDateTimeValue(hotelConnectCertificateUploadedAt)}</span>
                          </div>
                        </li>
                      }
                      {hasHotelConnectAccommodationId &&
                        <li>
                          <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                            <span>{t('hotelconnect_accommodation_id_label')}</span>
                            <span className="p-text-bold">{hotelConnectAccommodationId}</span>
                          </div>
                        </li>
                      }
                    </ul>
                  </div>
                </div>
                <hr className={"p-my-3"}/>
                <h3 className={"p-mt-5 p-mb-0"}>{t('ntak_daily_closure_sync_title')}</h3>
                <div className={"p-grid"}>
                  <div className={"p-col-12"}>
                    <ul className="p-pl-0 p-mt-3 p-mb-0" style={{listStyle: 'none'}}>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          <span>{t('daily_closure_current_status_label')}</span>
                          <span
                            className="p-badge"
                            style={{
                              ...getDailyClosureStatusMeta(dailyClosureStatus?.status).style,
                              width: '110px',
                              minWidth: '110px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700
                            }}
                          >
                            {getDailyClosureStatusMeta(dailyClosureStatus?.status).label}
                          </span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          <span>{t('daily_closure_last_closed_day_label')}</span>
                          <span className="p-text-bold">{dailyClosureStatus?.lastClosedDay || '-'}</span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          {renderLabelWithErrorIcon(
                            'daily-closure-last-attempt',
                            t('daily_closure_last_attempt_status_label'),
                            dailyClosureStatus?.lastAttempt?.error
                          )}
                          <span
                            className="p-badge"
                            style={{
                              ...getDailyClosureAttemptStatusMeta(dailyClosureStatus?.lastAttempt?.status).style,
                              width: '110px',
                              minWidth: '110px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700
                            }}
                          >
                            {getDailyClosureAttemptStatusMeta(dailyClosureStatus?.lastAttempt?.status).label}
                          </span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          <span>{t('daily_closure_last_attempt_happened_at_label')}</span>
                          <span className="p-text-bold">{formatDailyClosureDateTimeValue(dailyClosureStatus?.lastAttempt?.happenedAt)}</span>
                        </div>
                      </li>
                      <li className="p-mb-3">
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          <span>{t('daily_closure_next_run_at_label')}</span>
                          <span className="p-text-bold">{formatDailyClosureDateTimeValue(dailyClosureStatus?.nextRunAt)}</span>
                        </div>
                      </li>
                      <li>
                        <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap" style={{gap: '0.5rem'}}>
                          <span>{t('daily_closure_next_sync_dates_label')}</span>
                          <span className="p-text-bold">
                            {(dailyClosureStatus?.willSyncDates || []).length > 0
                              ? dailyClosureStatus.willSyncDates.join(', ')
                              : '-'}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            }
          </Panel>
        </TabPanel>
      </TabView>
    </>
  )
}
export default withTranslation('common')(_FM_AccommodationShowComponent);
